import React, { useState } from 'react';
import { Box, Container, Typography, TextField, Button, Paper, CircularProgress } from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';

import { apiService } from '../services/api';
import { SiteAnalysisResult } from '../types';

const FigmaColors = {
  background: '#FFFFFF',
  darkText: '#000000',
  inputBg: 'rgb(248, 242, 255)',
  inputPlaceholder: '#000000',
  primaryButton: '#4d648d',
  primaryButtonGradient: 'linear-gradient(100deg, #40ddff -6.08%, #7612fa 25.08%, #fa12e3)',
  spacerDark: '#d0e1f9',
};

const OnboardingPage: React.FC = () => {
  const [url, setUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<SiteAnalysisResult | null>(null);


  const handleAnalyze = async () => {
    setError(null);
    setIsAnalyzing(true);
    setAnalysisResult(null);

    try {
      // Basic URL validation
      const normalized = url.trim();
      const hasProtocol = /^https?:\/\//i.test(normalized);
      const candidate = hasProtocol ? normalized : `https://${normalized}`;
      
      // Validate URL format
      new URL(candidate);

      // Analyze the site using the API
      const result: SiteAnalysisResult = await apiService.analyzeSite(candidate);
      setAnalysisResult(result);

    } catch (urlError) {
      if (urlError instanceof TypeError) {
        setError('Введите корректную ссылку на сайт');
      } else {
        setError('Не удалось проанализировать сайт. Попробуйте еще раз.');
        console.error('Site analysis error:', urlError);
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: FigmaColors.background, color: FigmaColors.darkText }}>
      <Container maxWidth="sm" sx={{ py: 2 }}>
        {/* Top bar with icon */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, pt: 2 }}>
          <Paper
            elevation={0}
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'transparent',
              color: FigmaColors.darkText,
            }}
          >
            <LinkIcon />
          </Paper>
        </Box>

        {/* Title */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', px: 2, mt: 2 }}>
          <Typography variant="h6" align="center" sx={{ fontWeight: 700 }}>
            Быстрый запуск рекламы за 5 минут
          </Typography>
        </Box>

        {/* Question */}
        <Box sx={{ px: 2, mt: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Как нам найти Ваш сайт?
          </Typography>
        </Box>

        {/* URL input styled like Figma */}
        <Box sx={{ px: 2, mt: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: 2,
              py: 2,
              borderRadius: '36px',
              bgcolor: FigmaColors.inputBg,
            }}
          >
            <TextField
              fullWidth
              placeholder="example.com или https://example.com"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              variant="standard"
              InputProps={{
                disableUnderline: true,
                sx: {
                  color: FigmaColors.inputPlaceholder,
                  '::placeholder': { color: FigmaColors.inputPlaceholder, opacity: 1 },
                },
              }}
              inputProps={{ 'aria-label': 'site-url' }}
            />
          </Box>
          {error && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
              {error}
            </Typography>
          )}
        </Box>

        {/* Analyze button */}
        <Box sx={{ px: 2, mt: 2 }}>
                  <Button
          variant="contained"
          size="large"
          fullWidth
          onClick={handleAnalyze}
          disabled={isAnalyzing || !url.trim()}
          sx={{
            background: FigmaColors.primaryButtonGradient,
            color: '#ffffff !important',
            boxShadow: 'none',
            '&:hover': { 
              background: FigmaColors.primaryButtonGradient,
              opacity: 0.9,
              boxShadow: 'none',
              color: '#ffffff !important'
            },
            borderRadius: 3,
            height: 48,
            fontWeight: 700,
          }}
        >
          {isAnalyzing ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1, color: '#ffffff' }} />
              Анализируем сайт...
            </>
          ) : (
            'Анализировать веб-сайт'
          )}
        </Button>
        </Box>

        {/* Analysis Result */}
        {analysisResult && (
          <Box sx={{ px: 2, mt: 3 }}>
            <Paper elevation={0} sx={{ p: 2, bgcolor: FigmaColors.inputBg, borderRadius: '36px' }}>
              <Typography variant="h6" sx={{ color: '#000000', mb: 2, fontWeight: 700 }}>
                Результат анализа сайта
              </Typography>
              
              <Typography variant="body2" sx={{ color: FigmaColors.inputPlaceholder, mb: 2 }}>
                <strong>Описание:</strong> {analysisResult.summary}
              </Typography>
              
              <Typography variant="body2" sx={{ color: FigmaColors.inputPlaceholder, mb: 2 }}>
                <strong>Источник анализа:</strong> {analysisResult.source === 'ai' ? 'ИИ-анализ' : 'Эвристический анализ'}
              </Typography>
              
              <Typography variant="body2" sx={{ color: FigmaColors.inputPlaceholder, mb: 2 }}>
                <strong>Язык:</strong> {analysisResult.lang}
              </Typography>

              <Box sx={{ mt: 2, p: 1, bgcolor: '#ffffff', borderRadius: 1 }}>
                <Typography variant="caption" sx={{ color: FigmaColors.inputPlaceholder, display: 'block', mb: 1 }}>
                  Сырые данные (JSON):
                </Typography>
                <Typography 
                  component="pre" 
                  sx={{ 
                    fontFamily: 'monospace',
                    fontSize: '0.7rem',
                    color: FigmaColors.inputPlaceholder,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                    margin: 0
                  }}
                >
                  {JSON.stringify(analysisResult, null, 2)}
                </Typography>
              </Box>
            </Paper>
          </Box>
        )}

        {/* Spacer bar from Figma */}
        <Box sx={{ height: 20, mt: 2, bgcolor: FigmaColors.spacerDark, borderRadius: 1 }} />
      </Container>
    </Box>
  );
};

export default OnboardingPage;


